import { Box, Button, Divider, Step, StepButton, Stepper } from "@mui/material";
import { isEmpty } from "lodash-es";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { ulid } from "ulid";

import { useApplyForUserMutation, useSubmitPartnerApplicationMutation } from "../../queries/accounts";
import { Routes } from "../../Routes";
import { EVENTS, Segment } from "../../services/segment";
import { assertNever } from "../../utils/assertNever";
import { AdditionalInformationForm } from "./AdditionalInformationForm";
import { BeneficialOwnersForm, formSchema as ownerInformationFormSchema } from "./BeneficialOwnersForm";
import { BusinessInformationForm, formSchema as businessInformationFormSchema } from "./BusinessInformationForm";
import { FinalizeApplication } from "./FinalizeApplication";
import { FinalizePartnerApplication } from "./FinalizePartnerApplication";
import {
    IAdditionalInformationFormData,
    IBeneficialOwnersFormData,
    IBusinessInformationFormData,
    IOfficerInformationFormData,
} from "./FormData";
import { createUnitApplicationPayload } from "./KycUtils";
import { formSchema as officerInformationFormSchema, OfficerInformationForm } from "./OfficerInformationForm";
import { PhoneVerificationForm } from "./PhoneVerificationForm";
import { IWelcomeFormData, WelcomePage } from "./WelcomePage";

export enum KycApplicationStep {
    Welcome = 0,
    BusinessInformation,
    ContactAndOfficerInformation,
    BeneficialOwners,
    AdditionalInformation,
    PhoneVerification,
    Finalize,
}

export interface IApplicationFormData {
    welcomeData: IWelcomeFormData;
    businessData: IBusinessInformationFormData;
    officerData: IOfficerInformationFormData;
    ownersData: IBeneficialOwnersFormData;
    additionalInformationData: IAdditionalInformationFormData;
}

interface IProps {
    inviteCode?: string;
    defaultData?: Partial<IApplicationFormData>;
    initialStep?: KycApplicationStep;
    partnerApplicationToken?: string;
}

export const KycApplication: React.FC<IProps> = ({ inviteCode, defaultData, initialStep, partnerApplicationToken }) => {
    const firstPartyIovationInputRef = useRef<HTMLInputElement>(null);
    const thirdPartyIovationInputRef = useRef<HTMLInputElement>(null);

    const [welcomeData, setWelcomeData] = useState<IWelcomeFormData>(
        defaultData?.welcomeData ?? { tags: { promo_code: inviteCode } },
    );
    const [businessData, setBusinessData] = useState<IBusinessInformationFormData | undefined>(
        defaultData?.businessData,
    );
    const [officerData, setOfficerData] = useState<IOfficerInformationFormData | undefined>(defaultData?.officerData);
    const [ownersData, setOwnersData] = useState<IBeneficialOwnersFormData | undefined>(defaultData?.ownersData);
    const [additionalInformationData, setAdditionalInformationData] = useState<
        IAdditionalInformationFormData | undefined
    >(defaultData?.additionalInformationData);
    // No phone verification needed for partner applications
    const [isPhoneVerified, setIsPhoneVerified] = useState(partnerApplicationToken != null);

    const { isLoading, isError, data: application, mutate: applyForUser } = useApplyForUserMutation();
    const {
        isLoading: isPartnerLoading,
        isError: isPartnerError,
        data: partnerApplication,
        mutate: submitPartnerApplication,
    } = useSubmitPartnerApplicationMutation();

    const [page, setPage] = useState<KycApplicationStep>(initialStep ?? KycApplicationStep.Welcome);
    const nextPage = () => setPage((currentPage) => currentPage + 1);
    const prevPage = () => setPage((currentPage) => currentPage - 1);

    const idempotencyKey = useRef(ulid());

    const handleSubmitWelcomeInfo = (data: IWelcomeFormData) => {
        setWelcomeData(data);
        Segment.track(EVENTS.APPLY_SUBMIT_LETS_GET_STARTED, { promo_code: data.tags.promo_code });
        nextPage();
    };

    const handleSubmitBusinessInfo = (data: IBusinessInformationFormData) => {
        setBusinessData(data);
        Segment.track(EVENTS.APPLY_SUBMIT_BUSINESS_INFO, { no_ein: data.noEin });
        nextPage();
    };

    const handleSubmitOfficerInfo = (data: IOfficerInformationFormData) => {
        setOfficerData(data);
        Segment.track(EVENTS.APPLY_SUBMIT_OFFICER_INFO, { no_ssn: data.usePassport });
        nextPage();
    };

    const handleSubmitBeneficialOwners = (data: IBeneficialOwnersFormData) => {
        setOwnersData(data);

        if (officerData == null) {
            // Should never happen.
            return;
        }

        const needsAdditionalInformation = hasNonUSOfficerOrOwner(officerData, data);
        if (needsAdditionalInformation) {
            nextPage();
        } else {
            if (!isPhoneVerified) {
                // skip additional information
                setPage(KycApplicationStep.PhoneVerification);
            } else {
                setPage(KycApplicationStep.Finalize);
            }
        }
    };

    const handleSubmitAdditionalInformation = (data: IAdditionalInformationFormData) => {
        setAdditionalInformationData(data);
        if (!isPhoneVerified) {
            nextPage();
        } else {
            setPage(KycApplicationStep.Finalize);
        }
    };

    const handlePhoneVerification = () => {
        setIsPhoneVerified(true);
    };

    useEffect(() => {
        const businessDataIsValid = businessData && businessInformationFormSchema.isValidSync(businessData);
        const officerDataIsValid = officerData && officerInformationFormSchema.isValidSync(officerData);
        const ownersDataIsValid = ownersData && ownerInformationFormSchema.isValidSync(ownersData);

        if (!welcomeData || !businessDataIsValid || !officerDataIsValid || !ownersDataIsValid || !isPhoneVerified) {
            return;
        }

        const needsAdditionalInformation = hasNonUSOfficerOrOwner(officerData, ownersData);

        if (needsAdditionalInformation && additionalInformationData == null) {
            return;
        }

        Segment.track(EVENTS.APPLY_SUBMIT_APPLICATION);

        const payload = createUnitApplicationPayload(
            welcomeData,
            businessData,
            officerData,
            ownersData,
            additionalInformationData,
            idempotencyKey.current,
            thirdPartyIovationInputRef.current?.value || firstPartyIovationInputRef.current?.value,
        );

        if (partnerApplicationToken) {
            submitPartnerApplication({ application: payload, token: partnerApplicationToken });
        } else {
            applyForUser(payload);
            nextPage();
        }
    }, [
        applyForUser,
        submitPartnerApplication,
        welcomeData,
        businessData,
        officerData,
        ownersData,
        additionalInformationData,
        isPhoneVerified,
        partnerApplicationToken,
    ]);

    const router = useRouter();
    useEffect(() => {
        if (application?.id != null) {
            router.push(Routes.KycApply.Application({ applicationId: application.id }));
        }
    }, [application, router]);

    useEffect(() => {
        if (partnerApplication?.reset_pw_url != null) {
            window.location.href = partnerApplication.reset_pw_url;
        }
    }, [partnerApplication]);

    function renderStep(step: number, label: string) {
        const completed = page > step;
        const submitted = page === KycApplicationStep.Finalize;

        return (
            <Step completed={completed}>
                <StepButton color="inherit" disabled={!completed || submitted} onClick={() => setPage(step)}>
                    {label}
                </StepButton>
            </Step>
        );
    }

    function renderContent() {
        const nextPageButton = (
            <Button type="submit" variant="contained">
                Next Page &gt;
            </Button>
        );
        const prevPageButton = (
            <Button onClick={prevPage} variant="outlined">
                &lt; Prev Page
            </Button>
        );

        switch (page) {
            case KycApplicationStep.Welcome:
                return (
                    <WelcomePage
                        defaultValues={welcomeData}
                        onSubmit={handleSubmitWelcomeInfo}
                        nextPageButton={
                            <Button type="submit" variant="contained">
                                {"Let's get started!"}
                            </Button>
                        }
                    />
                );
            case KycApplicationStep.BusinessInformation:
                return (
                    <BusinessInformationForm
                        defaultValues={businessData}
                        onSubmit={handleSubmitBusinessInfo}
                        prevPageButton={!partnerApplicationToken && prevPageButton}
                        nextPageButton={nextPageButton}
                    />
                );
            case KycApplicationStep.ContactAndOfficerInformation:
                return (
                    <OfficerInformationForm
                        defaultValues={officerData}
                        onSubmit={handleSubmitOfficerInfo}
                        prevPageButton={prevPageButton}
                        nextPageButton={nextPageButton}
                    />
                );
            case KycApplicationStep.BeneficialOwners:
                return (
                    <BeneficialOwnersForm
                        defaultValues={ownersData}
                        officerData={officerData}
                        onSubmit={handleSubmitBeneficialOwners}
                        prevPageButton={prevPageButton}
                        nextPageButton={nextPageButton}
                    />
                );
            case KycApplicationStep.AdditionalInformation: {
                if (officerData == null || ownersData == null) {
                    // Should never happen.
                    return;
                }
                return (
                    <AdditionalInformationForm
                        defaultValues={additionalInformationData}
                        officerData={officerData}
                        beneficialOwnersData={ownersData}
                        onSubmit={handleSubmitAdditionalInformation}
                        prevPageButton={prevPageButton}
                        nextPageButton={nextPageButton}
                    />
                );
            }
            case KycApplicationStep.PhoneVerification: {
                if (officerData == null || ownersData == null) {
                    // Should never happen.
                    return;
                }

                const showAdditionalInformationPage = hasNonUSOfficerOrOwner(officerData, ownersData);
                return (
                    <PhoneVerificationForm
                        officerData={officerData}
                        onSubmit={handlePhoneVerification}
                        prevPageButton={
                            showAdditionalInformationPage ? (
                                prevPageButton
                            ) : (
                                <Button
                                    onClick={() => {
                                        setPage(KycApplicationStep.BeneficialOwners);
                                    }}
                                    variant="outlined"
                                >
                                    &lt; Prev Page
                                </Button>
                            )
                        }
                    />
                );
            }
            case KycApplicationStep.Finalize:
                return partnerApplicationToken ? (
                    <FinalizePartnerApplication isError={isPartnerError} isLoading={isPartnerLoading} />
                ) : (
                    <FinalizeApplication isError={isError} isLoading={isLoading} application={application} />
                );
            default:
                return assertNever(page);
        }
    }

    return (
        <>
            {/* input ids must match iovation/config.js */}
            <input id="first-party-blackbox" ref={firstPartyIovationInputRef} type="hidden" />
            <input id="third-party-blackbox" ref={thirdPartyIovationInputRef} type="hidden" />
            <Stepper activeStep={page} alternativeLabel={true}>
                {renderStep(KycApplicationStep.Welcome, "Welcome")}
                {renderStep(KycApplicationStep.BusinessInformation, "Business Information")}
                {renderStep(KycApplicationStep.ContactAndOfficerInformation, "Contact and Officer Information")}
                {renderStep(KycApplicationStep.BeneficialOwners, "Beneficial Owners")}
            </Stepper>
            <Box py={2}>
                <Divider />
            </Box>
            {renderContent()}
        </>
    );
};

function hasNonUSOfficerOrOwner(
    officerData: IOfficerInformationFormData,
    beneficialOwnersData: IBeneficialOwnersFormData,
): boolean {
    return (
        (isEmpty(officerData.ssn) && officerData.nationality !== "US") ||
        beneficialOwnersData.beneficialOwner.some((owner) => isEmpty(owner.ssn) && owner.nationality !== "US")
    );
}
